/*
  LX Over ons: schuifrij voor O04 (samenwerkingen).
  Markup: <lx-schuifrij> met [data-venster] (de schuivende rij), daarin [data-rij] met [data-kaart] (het vak)
  en [data-kaart-binnen] (de kaart), [data-stip="i"] (mobiel) en [data-pijlen] met [data-vorige]/[data-volgende]
  (desktop).

  Met data-uitlichten (Dion 12-10-2026, werklijst punt 20; Figma 802:6812 en 801:18634):
  - één kaart is groot (.is-actief), de rest staat op 85%; de maten staan in CSS (--lx-samen-b, -groot, -klein),
    het vak en de kaart veranderen in 200 ms tegelijk;
  - bij het laden is de eerste kaart groot (Grolsch);
  - desktop (muis): de kaart onder de muis wordt groot, de vorige gaat terug;
  - mobiel: na het vegen wordt de kaart in het midden van de rij groot (aan het begin de eerste, aan het eind
    de laatste);
  - pijlen (desktop): de volgende of vorige kaart wordt groot en schuift in beeld, doorlopend (na de laatste
    weer de eerste); verborgen als alles past;
  - stippen (mobiel): volgen de grote kaart; een tik maakt die kaart groot en zet hem in het midden;
  - alle kaarten krijgen de hoogte van de hoogste (--lx-samen-h), gemeten na het laden van de letters.
  Zonder data-uitlichten: de stippen volgen de kaart die vooraan staat (oud gedrag).
  Wordt door de sectie geladen; de bewaking hieronder voorkomt een tweede definitie.
*/
(function () {
  if (customElements.get('lx-schuifrij')) return;

  var desktop = function () {
    return window.matchMedia('(min-width: 1024px)').matches;
  };
  var rustig = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  class LxSchuifrij extends HTMLElement {
    connectedCallback() {
      if (this._klaar) return;
      this.venster = this.querySelector('[data-venster]');
      if (!this.venster) return;
      this._klaar = true;
      this.rij = this.querySelector('[data-rij]') || this.venster.firstElementChild;
      this.kaarten = Array.from(this.venster.querySelectorAll('[data-kaart]'));
      this.stippen = Array.from(this.querySelectorAll('[data-stip]'));
      this.pijlen = this.querySelector('[data-pijlen]');
      this.uitlichten = this.hasAttribute('data-uitlichten');
      this.actief = -1;
      this.gepland = false;
      this.stilTot = 0;

      this.opScroll = () => {
        if (this.uitlichten) {
          if (desktop() || Date.now() < this.stilTot) return;
          clearTimeout(this.scrollTimer);
          this.scrollTimer = setTimeout(() => this.kiesMidden(), 140);
          return;
        }
        if (this.gepland) return;
        this.gepland = true;
        requestAnimationFrame(() => {
          this.gepland = false;
          this.werkStippenBij();
        });
      };
      this.opKlik = (event) => {
        const doel = event.target instanceof Element ? event.target : null;
        if (!doel) return;
        const stip = doel.closest('[data-stip]');
        if (stip && this.contains(stip)) {
          this.gaNaar(Number(stip.dataset.stip));
          return;
        }
        if (doel.closest('[data-volgende]')) this.stap(1);
        else if (doel.closest('[data-vorige]')) this.stap(-1);
      };
      this.opMuis = (event) => {
        if (!desktop()) return;
        const kaart = event.target instanceof Element ? event.target.closest('[data-kaart]') : null;
        const index = kaart ? this.kaarten.indexOf(kaart) : -1;
        if (index > -1) this.zetActief(index);
      };
      this.opFocus = (event) => {
        const doel = event.target instanceof Element ? event.target : null;
        if (!doel || !doel.matches(':focus-visible')) return;
        const kaart = doel.closest('[data-kaart]');
        const index = kaart ? this.kaarten.indexOf(kaart) : -1;
        if (index > -1) this.zetActief(index);
      };
      this.opBlokKeuze = (event) => {
        const kaart = event.target && event.target.closest ? event.target.closest('[data-kaart]') : null;
        const index = kaart ? this.kaarten.indexOf(kaart) : -1;
        if (index > -1) this.gaNaar(index, true);
      };
      this.opMaat = () => {
        clearTimeout(this.maatTimer);
        this.maatTimer = setTimeout(() => this.meet(), 150);
      };

      this.venster.addEventListener('scroll', this.opScroll, { passive: true });
      this.addEventListener('click', this.opKlik);
      document.addEventListener('shopify:block:select', this.opBlokKeuze);

      if (this.uitlichten) {
        this.kaarten.forEach((kaart) => kaart.addEventListener('mouseenter', this.opMuis));
        this.addEventListener('focusin', this.opFocus);
        window.addEventListener('resize', this.opMaat);
        const start = this.kaarten.findIndex((k) => k.classList.contains('is-actief'));
        this.zetActief(start > -1 ? start : 0);
        this.meet();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.meet());
        window.addEventListener('load', this.opMaat, { once: true });
      } else {
        this.werkStippenBij();
      }
    }

    disconnectedCallback() {
      if (!this._klaar) return;
      this._klaar = false;
      this.venster.removeEventListener('scroll', this.opScroll);
      this.removeEventListener('click', this.opKlik);
      document.removeEventListener('shopify:block:select', this.opBlokKeuze);
      if (this.uitlichten) {
        this.kaarten.forEach((kaart) => kaart.removeEventListener('mouseenter', this.opMuis));
        this.removeEventListener('focusin', this.opFocus);
        window.removeEventListener('resize', this.opMaat);
      }
    }

    /* Maten uit de CSS van de sectie: basisbreedte, schaal groot en klein, tussenruimte. */
    maten() {
      const stijl = getComputedStyle(this.rij);
      const b = parseFloat(stijl.getPropertyValue('--lx-samen-b')) || 280;
      const groot = parseFloat(stijl.getPropertyValue('--lx-samen-groot')) || 1;
      const klein = parseFloat(stijl.getPropertyValue('--lx-samen-klein')) || 0.85;
      const gap = parseFloat(stijl.columnGap || stijl.gap) || 0;
      return { b: b, groot: groot, klein: klein, gap: gap };
    }

    /* Alle kaarten even hoog: de hoogte van de hoogste kaart (zonder schaal). */
    meet() {
      if (!this.uitlichten || !this.rij) return;
      this.rij.classList.remove('is-gemeten');
      this.rij.style.removeProperty('--lx-samen-h');
      let hoogste = 0;
      this.kaarten.forEach((kaart) => {
        const binnen = kaart.querySelector('[data-kaart-binnen]');
        if (binnen) hoogste = Math.max(hoogste, binnen.offsetHeight);
      });
      if (hoogste > 0) {
        this.rij.style.setProperty('--lx-samen-h', hoogste + 'px');
        this.rij.classList.add('is-gemeten');
      }
      if (this.pijlen) this.pijlen.hidden = !(desktop() && this.venster.scrollWidth > this.venster.clientWidth + 1);
    }

    zetActief(index) {
      if (index < 0 || index >= this.kaarten.length) return;
      if (index !== this.actief) {
        this.actief = index;
        this.kaarten.forEach((kaart, i) => kaart.classList.toggle('is-actief', i === index));
        this.stilTot = Date.now() + 350;
      }
      this.stippen.forEach((stip, i) => {
        if (i === index) stip.setAttribute('aria-current', 'true');
        else stip.removeAttribute('aria-current');
      });
    }

    /* Waar een kaart begint als hij groot is (alle kaarten ervoor zijn dan klein). */
    beginGroot(index) {
      const m = this.maten();
      return index * (m.b * m.klein + m.gap);
    }

    gaNaar(index, direct) {
      if (!this.kaarten[index]) return;
      if (!this.uitlichten) {
        const links = this.kaarten[index].offsetLeft - this.kaarten[0].offsetLeft;
        this.venster.scrollTo({ left: links, behavior: direct || rustig() ? 'auto' : 'smooth' });
        return;
      }
      this.zetActief(index);
      const m = this.maten();
      let links = this.beginGroot(index);
      if (!desktop()) links = links + (m.b * m.groot) / 2 - this.venster.clientWidth / 2;
      links = Math.max(0, links);
      this.venster.scrollTo({ left: links, behavior: direct || rustig() ? 'auto' : 'smooth' });
    }

    stap(richting) {
      const n = this.kaarten.length;
      if (!n) return;
      const volgende = (((this.actief < 0 ? 0 : this.actief) + richting) % n + n) % n;
      this.gaNaar(volgende);
    }

    /* Mobiel: de kaart waarvan het midden het dichtst bij het midden van de rij staat. */
    kiesMidden() {
      if (desktop()) return;
      const links = this.venster.scrollLeft;
      const max = this.venster.scrollWidth - this.venster.clientWidth;
      let index = 0;
      if (max > 0 && links >= max - 2) {
        index = this.kaarten.length - 1;
      } else if (links > 2) {
        const midden = links + this.venster.clientWidth / 2;
        let kleinste = Infinity;
        this.kaarten.forEach((kaart, i) => {
          const afstand = Math.abs(kaart.offsetLeft + kaart.offsetWidth / 2 - midden);
          if (afstand < kleinste) {
            kleinste = afstand;
            index = i;
          }
        });
      }
      this.zetActief(index);
    }

    werkStippenBij() {
      if (!this.kaarten.length) return;
      const links = this.venster.scrollLeft;
      const max = this.venster.scrollWidth - this.venster.clientWidth;
      let index = 0;
      if (max > 0 && links >= max - 2) {
        index = this.kaarten.length - 1;
      } else {
        let kleinste = Infinity;
        this.kaarten.forEach((kaart, i) => {
          const afstand = Math.abs(kaart.offsetLeft - this.kaarten[0].offsetLeft - links);
          if (afstand < kleinste) {
            kleinste = afstand;
            index = i;
          }
        });
      }
      if (index === this.actief) return;
      this.actief = index;
      this.stippen.forEach((stip, i) => {
        if (i === index) stip.setAttribute('aria-current', 'true');
        else stip.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('lx-schuifrij', LxSchuifrij);
})();
